import { useEffect, useState } from "react";
import { Archive, Copy, Eye, History, Mail, Phone, Plus, X } from "lucide-react";
import type { Contact } from "@/lib/types";
import { formatCents, formatDate } from "@/lib/format";
import { brandByCode } from "@/lib/brands";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { BrandMark } from "@/components/BrandMark";
import { useToast } from "@/components/Toaster";
import { OPEN_TAG_PICKER, useTagChange, useTags } from "./hooks";
import { TagHistorySheet, AuditSheet } from "./HistorySheets";

export const mailStatus: Record<string, { label: string; variant: "success" | "warning" | "destructive" | "muted"; help: string }> = {
  ok: { label: "mailable", variant: "success", help: "Emails can go to this address." },
  unconfirmed: { label: "unconfirmed", variant: "warning", help: "Not confirmed yet." },
  opted_out: { label: "opted out", variant: "muted", help: "Unsubscribed; marketing email won't go here." },
  bounced: { label: "bounced", variant: "destructive", help: "Mail to this address bounced; nothing is sent." },
  complained: { label: "complaint", variant: "destructive", help: "Marked an email as spam; nothing is sent." },
  invalid: { label: "invalid", variant: "destructive", help: "Not a working address." },
  suppressed: { label: "suppressed", variant: "muted", help: "On the do-not-send list." },
};

const accessLabel: Record<string, string> = { club: "Investor's Club", manager_training: "Manager Training", courses: "Courses" };

export function ContactHeader({ c }: { c: Contact }) {
  const name = [c.prefix, c.first_name, c.middle_name, c.last_name, c.suffix].filter(Boolean).join(" ") || "(no name)";
  const primary = c.emails[0];
  const phone = c.phones[0];
  const toast = useToast();
  const [sheet, setSheet] = useState<"tags" | "audit" | null>(null);
  const copy = (text: string, what: string) => {
    void navigator.clipboard?.writeText(text).then(() => toast(`Copied ${what}`));
  };
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="min-w-[min(100%,20rem)] flex-1 space-y-2">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-semibold leading-tight">{name}</h1>
            {c.nickname && (
              <span className="text-[var(--color-muted-foreground)]" title="Emails greet them with this name">
                goes by <span className="text-[var(--color-foreground)]">{c.nickname}</span>
              </span>
            )}
            <button
              className="num inline-flex items-center gap-1 rounded px-1 text-sm text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
              title={c.keap_id ? "Contact ID (the same as the Keap ID). Click to copy." : "Contact ID. Click to copy."}
              onClick={() => copy(String(c.id), "contact ID")}
            >
              #{c.id}
              <Copy className="size-3 opacity-60" />
            </button>
            {c.archived && (
              <Badge variant="muted">
                <Archive className="size-3" /> archived: reference only, never emailed
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {c.brands.length > 0 && (
              <span className="flex flex-wrap items-center gap-2.5">
                {c.brands.map((b) => (
                  <BrandMark
                    key={b.brand_id}
                    code={b.code}
                    dim={b.marketing_status !== "subscribed"}
                    title={`${brandByCode(b.code)?.name ?? b.name}: ${b.marketing_status === "subscribed" ? "subscribed to marketing" : b.marketing_status === "unsubscribed" ? "unsubscribed from marketing" : "no marketing consent"}`}
                  />
                ))}
              </span>
            )}
            {c.access_summary.map((a) => (
              <Badge key={a} variant="success" title="Active access">
                {accessLabel[a] ?? a.replace(/_/g, " ")}
              </Badge>
            ))}
            {c.referred_by_affiliate_id && <Badge variant="default">referred by affiliate #{c.referred_by_affiliate_id}</Badge>}
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            {primary ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <Mail className="size-3.5 shrink-0 text-[var(--color-muted-foreground)]" />
                <a href={`mailto:${primary.email}`} className="truncate hover:underline">
                  {primary.email}
                </a>
                <Badge variant={mailStatus[primary.status]?.variant ?? "muted"} title={mailStatus[primary.status]?.help}>
                  {mailStatus[primary.status]?.label ?? primary.status}
                </Badge>
                {c.emails.length > 1 && <span className="text-xs text-[var(--color-muted-foreground)]">+{c.emails.length - 1} more</span>}
              </span>
            ) : (
              <span className="text-[var(--color-muted-foreground)]">No email address</span>
            )}
            {phone && (
              <span className="num flex items-center gap-1.5">
                <Phone className="size-3.5 text-[var(--color-muted-foreground)]" />
                <a href={`tel:${phone.e164 ?? phone.number}`} className="hover:underline">
                  {phone.number}
                </a>
                {phone.extension && <span>ext. {phone.extension}</span>}
                <span className="text-xs text-[var(--color-muted-foreground)]">{phone.type}</span>
              </span>
            )}
          </div>
          <TagChips c={c} />
          <div className="flex gap-3 text-xs">
            <button className="inline-flex items-center gap-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:underline" onClick={() => setSheet("tags")}>
              <History className="size-3.5" /> Tag history
            </button>
            <button className="inline-flex items-center gap-1 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:underline" onClick={() => setSheet("audit")}>
              <Eye className="size-3.5" /> Who viewed or changed this
            </button>
          </div>
        </div>

        <dl className="grid w-full grid-cols-[auto_auto] gap-x-5 gap-y-1 text-sm sm:w-auto sm:min-w-56">
          <dt className="text-[var(--color-muted-foreground)]">Lifetime value</dt>
          <dd className="num text-right text-lg font-semibold leading-tight">{formatCents(c.ltv_cents)}</dd>
          <dt className="text-[var(--color-muted-foreground)]">Orders</dt>
          <dd className="num text-right">{c.order_count}</dd>
          <dt className="text-[var(--color-muted-foreground)]">Last order</dt>
          <dd className="num text-right">{c.last_order_at ? `${formatCents(c.last_order_total_cents)}, ${formatDate(c.last_order_at)}` : "—"}</dd>
          <dt className="text-[var(--color-muted-foreground)]">Added</dt>
          <dd className="num text-right">{formatDate(c.created_at)}</dd>
        </dl>
      </div>
      <TagHistorySheet id={c.id} open={sheet === "tags"} onOpenChange={(o) => setSheet(o ? "tags" : null)} />
      <AuditSheet id={c.id} open={sheet === "audit"} onOpenChange={(o) => setSheet(o ? "audit" : null)} />
    </Card>
  );
}

function TagChips({ c }: { c: Contact }) {
  const change = useTagChange(c.id);
  const toast = useToast();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_TAG_PICKER, show);
    return () => window.removeEventListener(OPEN_TAG_PICKER, show);
  }, []);
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {c.tags.map((t) => (
        <Badge
          key={t.tag_id}
          variant={t.skip_marketing ? "warning" : "default"}
          title={`${t.category_name ? t.category_name + ": " : ""}added ${formatDate(t.applied_at)} by ${t.applied_by} (${t.source})${t.skip_marketing ? ". Left out of marketing email." : ""}`}
        >
          {t.name}
          <button
            aria-label={`Remove tag ${t.name}`}
            className="rounded-full hover:opacity-70"
            onClick={() => change.mutate({ tagId: t.tag_id, remove: true }, { onSuccess: () => toast(`Removed tag ${t.name}`) })}
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
      >
        <Plus className="size-3" /> Add tag
      </button>
      <TagPicker
        exclude={new Set(c.tags.map((t) => t.tag_id))}
        open={open}
        onOpenChange={setOpen}
        onPick={(id, name) => change.mutate({ tagId: id }, { onSuccess: () => toast(`Added tag ${name}`), onError: (e) => toast(e.message, "error") })}
      />
    </div>
  );
}

/** Searchable tag list in a small dialog (typing goes to the search box, not menu type-ahead). */
export function TagPicker({
  exclude,
  open,
  onOpenChange,
  onPick,
  title = "Add a tag",
}: {
  exclude?: Set<number>;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onPick: (tagId: number, name: string) => void;
  title?: string;
}) {
  const tags = useTags();
  const options = (tags.data?.tags ?? []).filter((t) => !exclude?.has(t.id));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <Command label={title}>
          <CommandInput autoFocus placeholder="Find a tag by name, category or ID…" />
          <CommandList>
            <CommandEmpty>No matching tags.</CommandEmpty>
            {options.map((t) => (
              <CommandItem
                key={t.id}
                value={`${t.name} ${t.category_name ?? ""} ${t.id}`}
                onSelect={() => {
                  onPick(t.id, t.name);
                  onOpenChange(false);
                }}
              >
                <span className="truncate">{t.name}</span>
                {t.skip_marketing && <Badge variant="warning">no marketing</Badge>}
                <span className="num ml-auto text-xs text-[var(--color-muted-foreground)]">
                  {t.category_name ?? ""} #{t.id}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
